import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useModal } from "@ebay/nice-modal-react"

import Form from "/src/components/form/Form";
import Field from '/src/components/UI/Field/Field';
import Button from "/src/components/UI/Button/Button";
import TextInput from "/src/components/UI/TextField/TextInput"
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import { useToastDispatchContext } from '/src/layouts/toast/context/toastContext';
import channelService from "../../../services/channelService"
import "./ChannelRcs.css"

const CREDENTIAL_FIELDS = ['provider_username', 'provider_password', 'provider_bot_name'];

const ChannelRcs = (({ channel }) => {

    const modal = useModal();

    const { onClose } = useModalHelper(modal);

    const toast = useToastDispatchContext();

    const { updateChannel, revokeRcsCredentials, revealRcsPassword } = channelService;

    // Two-step confirmation: the first click arms, the second revokes.
    const [confirmingRevocation, setConfirmingRevocation] = useState(false);
    const [revoking, setRevoking] = useState(false);

    // `revoking` is not yet set when setState returns: two quick clicks would both get past
    // the button's `disabled`.
    const revokeInFlight = useRef(false);

    // Plain-text password fetched when the eye is clicked; null until revealed.
    const [revealedPassword, setRevealedPassword] = useState(null);
    const [passwordShown, setPasswordShown] = useState(false);
    const revealInFlight = useRef(false);

    // The API returns the username and bot name; the password only comes as a hint, shown
    // as placeholder.
    const { register, handleSubmit, formState: { errors }, formState, getValues, setValue } = useForm({
        mode: "onChange",
        defaultValues: {
            name: channel.name,
            provider_username: channel.provider_username ?? "",
            provider_password: "",
            provider_bot_name: channel.provider_bot_name ?? ""
        }
    });

    // Stored values: resending them would change nothing but the rotation date.
    const unchangedValues = {
        provider_username: channel.provider_username ?? "",
        provider_password: revealedPassword ?? "",
        provider_bot_name: channel.provider_bot_name ?? ""
    };

    const onTogglePassword = async () => {

        if (passwordShown) {
            setPasswordShown(false);
            // Hidden again: the field reverts to the hint unless the operator typed something else.
            if (revealedPassword !== null && getValues("provider_password") === revealedPassword) {
                setValue("provider_password", "");
            }
            return;
        }

        // Nothing to fetch: the operator is typing, or no password is stored.
        if ((getValues("provider_password") || "") !== "" || !channel.provider_password_hint) {
            setPasswordShown(true);
            return;
        }

        if (revealInFlight.current) {
            return;
        }

        revealInFlight.current = true;
        const [response, error] = await revealRcsPassword(channel.id);
        revealInFlight.current = false;

        if (error !== null || !response?.provider_password) {
            console.log(error);
            toast.error('Error', "The password could not be revealed");
            return;
        }

        setRevealedPassword(response.provider_password);
        setValue("provider_password", response.provider_password);
        setPasswordShown(true);
    };

    const onSubmit = async () => {

        // A plain object, not FormData: it avoids the POST ?_method=PUT detour and leaves an
        // untouched key absent from the body. An absent key keeps the stored credential, an
        // empty string would be rejected.
        let dataToSend = { name: getValues("name") };

        // Trimmed like the server `filled` rule does: a blank-only field would be rejected, and a
        // validation failure surfaces here as a 500, not a 422.
        CREDENTIAL_FIELDS.forEach(field => {
            let value = (getValues(field) || "").trim();

            if (value && value !== unchangedValues[field]) {
                dataToSend[field] = value;
            }
        });

        const [response, error] = await updateChannel(channel.id, dataToSend);

        if (error !== null) {
            console.log(error);
            // The modal stays open: closing it would discard the credentials the operator just typed.
            toast.error('Error', error.data ? error.data : "The channel could not be updated");
            return;
        }

        modal.resolve(response);
        toast.success('Channel updated', 'The RCS channel has been updated !');
        onClose();
    };

    const onRevoke = async () => {

        if (confirmingRevocation == false) {
            setConfirmingRevocation(true);
            return;
        }

        if (revokeInFlight.current) {
            return;
        }

        revokeInFlight.current = true;
        setRevoking(true);

        const [response, error] = await revokeRcsCredentials(channel.id);

        revokeInFlight.current = false;
        setRevoking(false);

        if (error !== null) {
            console.log(error);
            // The modal stays open: nothing changed, the operator can retry.
            setConfirmingRevocation(false);
            toast.error('Error', error.data ? error.data : "The credentials could not be revoked");
            return;
        }

        // NiceModal resolves its promise only once: staying open would make the table miss any
        // later update.
        modal.resolve(response);
        toast.success('Credentials revoked', 'The RCS channel is no longer configured.');
        onClose();
    };

    return (
        <>
            <Form className="form-container_sideModal" onSubmit={handleSubmit(onSubmit)}>
                <Form.Title className="title_channelTel"> Channel RCS for bot {channel.bot?.name}</Form.Title>
                <Form.Body className="form-body_container_sideModal channel_form-body">
                    <br></br>
                    <div>
                        <Field label={<span> Name <span className="obligatory_field">*</span> </span>} listLabelClassName="title-weigth label_color">
                            <TextInput {...register('name', { required: true, minLength: 3 })} style={{ borderColor: errors.name ? "red" : "" }} />
                            <span className='error' style={{ marginTop: "-15px", fontSize: "14px" }}>{errors.name ? (errors.name.message ? errors.name.message : "At least 3 letters") : ""}</span>
                        </Field>
                    </div>
                    <div className="rcs_credentials">
                        <Field label="Route Mobile credentials" listLabelClassName="title_form title_dropdown" />
                        <span className={"rcs_credentials-status " + (channel.credentials_configured ? "rcs_credentials-configured" : "rcs_credentials-missing")}>
                            {channel.credentials_configured ? "Credentials configured" : "Credentials not configured"}
                        </span>
                        <span className="rcs_credentials-hint">
                            The eye reveals the password. A field left empty keeps the value currently in place,
                            and revoking is the only way to remove one.
                        </span>
                        <Field label="Route Mobile username" listLabelClassName="titles-input">
                            <TextInput {...register('provider_username')} autoComplete="off" />
                        </Field>
                        <Field label="Route Mobile password" listLabelClassName="titles-input">
                            {/* Not TextInput type="password": its eye toggles visibility without fetching
                                the stored password. */}
                            <div className="rcs_password">
                                <input
                                    {...register('provider_password')}
                                    type={passwordShown ? "text" : "password"}
                                    placeholder={channel.provider_password_hint ?? ""}
                                    autoComplete="new-password"
                                    className="input full-width input-space input_auth"
                                />
                                <i
                                    className={"icon_toggle_password " + (passwordShown ? "fas fa-eye-slash" : "fas fa-eye")}
                                    title={passwordShown ? "Hide the password" : "Show the password"}
                                    onClick={onTogglePassword}
                                ></i>
                            </div>
                        </Field>
                        <Field label="Route Mobile bot name" listLabelClassName="titles-input">
                            <TextInput {...register('provider_bot_name')} autoComplete="off" />
                        </Field>
                        {/* Offered whatever the displayed state: an unreadable ciphertext or a half-written
                            set reads "not configured" yet still holds columns to erase. */}
                        <div className="rcs_credentials-revoke">
                            <Button
                                id="form_rcs_revoke"
                                type="button"
                                label={revoking
                                    ? "Revoking..."
                                    : (confirmingRevocation ? "Confirm revocation" : "Revoke credentials")}
                                className={confirmingRevocation ? "rcs_revoke-confirm" : "rcs_revoke"}
                                disabled={revoking}
                                onClick={onRevoke}
                            />
                            {confirmingRevocation
                                ? <>
                                    <Button
                                        id="form_rcs_revoke_cancel"
                                        type="button"
                                        label="Cancel"
                                        className="rcs_revoke-cancel"
                                        disabled={revoking}
                                        onClick={() => setConfirmingRevocation(false)}
                                    />
                                    <span className="rcs_credentials-revoke-warning">
                                        Whatever is stored is erased, readable or not. The connector may keep sending
                                        for up to five minutes, until its cached credentials expire.
                                    </span>
                                </>
                                : null}
                        </div>
                    </div>
                    <div className="rcs_callback">
                        <Field label="Callback URL" listLabelClassName="title_form title_dropdown" />
                        <span className="rcs_callback-hint">
                            Declare this URL in the Route Mobile console. It is specific to this channel and never changes.
                        </span>
                        {channel.callback_url
                            ? <code
                                className="rcs_callback-url"
                                title="Click to copy"
                                onClick={() => navigator.clipboard.writeText(channel.callback_url).then(() => {
                                    toast.success('Callback URL copied', 'Paste it into the Route Mobile console.');
                                })}
                            >
                                {channel.callback_url}
                            </code>
                            : <span className="rcs_callback-missing">
                                Public address not configured on this environment. Route token: <code className="rcs_callback-token">{channel.callback_route_token ?? "not issued yet"}</code>
                            </span>}
                    </div>
                </Form.Body>
                <Form.Buttons className="rcs_buttons">
                    <Button id="form_rcs" type="submit" label="Update" className="validate" disabled={!formState.isValid} />
                </Form.Buttons>
            </Form>
        </>
    )
});

export default ChannelRcs;
